import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { useToast } from '../context/ToastContext';
import { apiClient } from '../api/client';
import { useAuth } from '../context/AuthContext';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

const AdminLogin = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { login } = useAuth();
  
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(loginSchema)
  });

  const onSubmit = async (data) => {
    try {
      const response = await apiClient.post('/auth/admin-login', data);
      const { access_token, role, email } = response.data;
      
      login({
        token: access_token,
        role: 'admin',
        email: email || data.email
      });
      
      addToast('Welcome to Admin Portal', 'success');
      navigate('/admin');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Invalid admin credentials', 'error');
    }
  };

  return (
    <div className="auth-page bg-gray-900" style={{ background: 'var(--gray-900)' }}>
      <div className="auth-card !bg-gray-800 !border-gray-700 !text-white shadow-2xl">
        <div className="auth-logo justify-center !text-white mb-8">
          <div className="w-10 h-10 rounded bg-primary text-white flex items-center justify-center font-bold text-lg mr-2">M</div>
          <div className="text-xl">Million<span className="text-primary">Minds</span></div>
        </div>
        
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold font-display">Admin Portal</h1>
          <p className="text-gray-400 mt-2 text-sm">Sign in to manage the platform</p>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">Admin Email</label>
            <input 
              type="email" 
              className="w-full bg-gray-700 border border-gray-600 rounded p-3 text-white focus:border-primary focus:outline-none"
              placeholder="admin@millionminds.com"
              {...register('email')}
            />
            {errors.email && <span className="text-xs text-red-400 mt-1 block">{errors.email.message}</span>}
          </div>
          
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
            <input 
              type="password" 
              className="w-full bg-gray-700 border border-gray-600 rounded p-3 text-white focus:border-primary focus:outline-none"
              placeholder="••••••••"
              {...register('password')}
            />
            {errors.password && <span className="text-xs text-red-400 mt-1 block">{errors.password.message}</span>}
          </div>
          
          <Button type="submit" variant="primary" className="w-full text-base py-3" isLoading={isSubmitting}>
            Authenticate
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
